import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Copy, RefreshCcw, Trash2, Plus, Mail, FileCheck,
    Search, ArrowUp, ArrowDown, MoreHorizontal, X,
} from "lucide-react";
import { usePermission } from "@/lib/usePermission";

// -------------------- constants --------------------

const STATUS_STYLES = {
    pending: "bg-amber-50 text-amber-900 ring-amber-200",
    opened: "bg-blue-50 text-blue-900 ring-blue-200",
    submitted: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    expired: "bg-slate-100 text-slate-600 ring-slate-200",
    cancelled: "bg-rose-50 text-rose-900 ring-rose-200",
    deleted: "bg-neutral-100 text-neutral-500 ring-neutral-300 italic",
};

// Chips shown in the filter row. Order matters visually.
const ALL_STATUSES = ["pending", "opened", "submitted", "expired", "cancelled"];
// Spec 3A: default selected = all EXCEPT cancelled
const DEFAULT_STATUSES = new Set(["pending", "opened", "submitted", "expired"]);

// Row-level delete allowed only for these two terminal states (spec 5B).
const DELETABLE_STATUSES = new Set(["expired", "cancelled"]);

// localStorage keys for sort persistence (spec 2B).
const LS_SORT_COL = "admin.invites.sortColumn";
const LS_SORT_DIR = "admin.invites.sortDirection";

// -------------------- helpers --------------------

function StatusBadge({ status }) {
    return (
        <span
            data-testid={`invite-status-${status}`}
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}
        >
            {status}
        </span>
    );
}

function fmtDate(s) {
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
}

function SortHeader({ label, column, sortColumn, sortDirection, onToggle, testid }) {
    const active = sortColumn === column;
    const Icon = active && sortDirection === "asc" ? ArrowUp : ArrowDown;
    return (
        <TableHead
            className="cursor-pointer select-none hover:text-slate-900"
            onClick={() => onToggle(column)}
            data-testid={testid || `invites-sort-${column}`}
        >
            <span className="inline-flex items-center gap-1.5">
                {label}
                <Icon
                    size={13}
                    className={active ? "text-blue-900" : "text-slate-300"}
                />
            </span>
        </TableHead>
    );
}

function StatusChip({ status, active, onToggle }) {
    return (
        <button
            type="button"
            onClick={() => onToggle(status)}
            data-testid={`invites-filter-chip-${status}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize ring-1 transition ${
                active
                    ? `${STATUS_STYLES[status] || "bg-slate-200 text-slate-800 ring-slate-300"} ring-inset shadow-sm`
                    : "bg-white text-slate-500 ring-slate-200 hover:ring-slate-300 hover:text-slate-700"
            }`}
        >
            {status}
            {active && <X size={11} className="opacity-60" />}
        </button>
    );
}

// -------------------- page --------------------

export default function InvitesListPage() {
    // data
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    // controls
    const [rawQuery, setRawQuery] = useState("");
    const [query, setQuery] = useState("");
    const [sortColumn, setSortColumn] = useState(() => localStorage.getItem(LS_SORT_COL) || "created");
    const [sortDirection, setSortDirection] = useState(() => localStorage.getItem(LS_SORT_DIR) || "desc");
    const [activeStatuses, setActiveStatuses] = useState(() => new Set(DEFAULT_STATUSES));

    // selection + modals
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);

    // legacy Revoke (existing "Cancel invite" action — unchanged)
    const { hasPermission, ready: permReady } = usePermission();
    const canDelete = permReady && hasPermission("Invites", "Delete");

    // 300ms search debounce (spec)
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setQuery(rawQuery.trim()), 300);
        return () => clearTimeout(debounceRef.current);
    }, [rawQuery]);

    // Persist sort choice to localStorage
    useEffect(() => { localStorage.setItem(LS_SORT_COL, sortColumn); }, [sortColumn]);
    useEffect(() => { localStorage.setItem(LS_SORT_DIR, sortDirection); }, [sortDirection]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (query) params.set("q", query);
            params.set("sort", sortColumn);
            params.set("order", sortDirection);
            for (const s of activeStatuses) params.append("statuses", s);
            const { data } = await api.get(`/admin/invites?${params.toString()}`);
            setInvites(data);
            // Trim selection to visible rows
            setSelectedIds((prev) => {
                const visible = new Set(data.map((d) => d.id));
                const kept = new Set();
                for (const id of prev) if (visible.has(id)) kept.add(id);
                return kept;
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [query, sortColumn, sortDirection, activeStatuses]);

    useEffect(() => { load(); }, [load]);

    // -------------------- interactions --------------------

    const toggleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            // Default direction when switching columns: created/expires → desc, name/email/status → asc
            setSortDirection(column === "created" || column === "expires" ? "desc" : "asc");
        }
    };

    const toggleStatus = (status) => {
        setActiveStatuses((prev) => {
            const next = new Set(prev);
            if (next.has(status)) next.delete(status); else next.add(status);
            return next;
        });
    };

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Invite link copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    const resend = async (id) => {
        setBusy(true);
        try {
            await api.post(`/admin/invites/${id}/resend`);
            toast.success("Invite resent (mock email logged)");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const confirmRevoke = async () => {
        if (!revokeTarget) return;
        setBusy(true);
        try {
            await api.delete(`/admin/invites/${revokeTarget.id}`);
            toast.success("Invite revoked");
            setRevokeTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // -------------------- delete (Phase INVITES-MANAGE) --------------------

    const confirmDeleteSingle = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            await api.post(`/admin/invites/${deleteTarget.id}/delete`);
            toast.success(`Deleted invite for ${deleteTarget.customer_name}`);
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/invites/bulk-delete", {
                invite_ids: Array.from(selectedIds),
            });
            const { deleted = [], skipped = [], errors = [] } = data || {};
            if (deleted.length > 0) toast.success(`Deleted ${deleted.length} invite${deleted.length === 1 ? "" : "s"}`);
            if (skipped.length > 0) {
                toast.warning(
                    `${skipped.length} skipped: ${skipped
                        .slice(0, 3)
                        .map((s) => s.reason)
                        .join(", ")}${skipped.length > 3 ? " …" : ""}`,
                );
            }
            if (errors.length > 0) toast.error(`${errors.length} errored — check logs`);
            setBulkDeleteOpen(false);
            setSelectedIds(new Set());
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // -------------------- selection helpers --------------------

    const selectableRows = useMemo(
        () => invites.filter((i) => DELETABLE_STATUSES.has(i.status)),
        [invites],
    );
    const allSelectableChecked =
        selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id));
    const someSelectableChecked =
        !allSelectableChecked && selectableRows.some((r) => selectedIds.has(r.id));

    const toggleRow = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllOnPage = () => {
        setSelectedIds((prev) => {
            if (allSelectableChecked) {
                // uncheck all deletable
                const next = new Set(prev);
                for (const r of selectableRows) next.delete(r.id);
                return next;
            } else {
                const next = new Set(prev);
                for (const r of selectableRows) next.add(r.id);
                return next;
            }
        });
    };

    // Preview of selected customers for the bulk-delete modal (spec 7B).
    const selectedPreview = useMemo(() => {
        const idSet = selectedIds;
        const names = invites
            .filter((i) => idSet.has(i.id))
            .map((i) => i.customer_name);
        return { names, first5: names.slice(0, 5), extra: Math.max(0, names.length - 5) };
    }, [selectedIds, invites]);

    // -------------------- render --------------------

    const showBulkBar = canDelete && selectedIds.size > 0;

    return (
        <DashboardShell>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                        Operations · Invites
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Customer invites
                    </h1>
                    <p className="mt-2 max-w-xl text-sm text-slate-600 leading-relaxed">
                        Send a tailored application link to a prospective rental customer. They&apos;ll receive an email and complete only the fields you mark required.
                    </p>
                </div>
                <Button asChild className="bg-blue-900 hover:bg-blue-800 gap-2">
                    <Link to="/admin/invites/new" data-testid="new-invite-button">
                        <Plus size={16} /> New invite
                    </Link>
                </Button>
            </div>

            {/* Search + filter row */}
            <div className="mt-8 flex flex-col gap-3">
                <div className="relative max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="invites-search-input"
                        value={rawQuery}
                        onChange={(e) => setRawQuery(e.target.value)}
                        placeholder="Search by name or email…"
                        className="pl-9"
                    />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wider text-slate-400 mr-2">
                        Status
                    </span>
                    {ALL_STATUSES.map((s) => (
                        <StatusChip
                            key={s}
                            status={s}
                            active={activeStatuses.has(s)}
                            onToggle={toggleStatus}
                        />
                    ))}
                </div>
            </div>

            {/* Bulk action bar */}
            {showBulkBar && (
                <div
                    data-testid="invites-bulk-action-bar"
                    className="mt-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-2.5"
                >
                    <span className="text-sm text-blue-900 font-medium">
                        {selectedIds.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedIds(new Set())}
                            data-testid="invites-bulk-clear"
                        >
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setBulkDeleteOpen(true)}
                            className="gap-1.5 bg-red-600 hover:bg-red-700"
                            data-testid="invites-bulk-delete-open"
                        >
                            <Trash2 size={14} /> Delete selected
                        </Button>
                    </div>
                </div>
            )}

            <Card className="mt-4">
                <CardContent className="p-0">
                    <Table data-testid="invites-table">
                        <TableHeader>
                            <TableRow>
                                {canDelete && (
                                    <TableHead className="w-10">
                                        <Checkbox
                                            data-testid="invites-select-all"
                                            checked={allSelectableChecked}
                                            // shadcn checkbox supports "indeterminate" via data attribute — fall back to unchecked visual
                                            data-indeterminate={someSelectableChecked ? "true" : undefined}
                                            onCheckedChange={toggleAllOnPage}
                                            disabled={selectableRows.length === 0}
                                            aria-label="Select all deletable invites on page"
                                        />
                                    </TableHead>
                                )}
                                <SortHeader label="Customer" column="customer_name" sortColumn={sortColumn} sortDirection={sortDirection} onToggle={toggleSort} />
                                <SortHeader label="Email" column="customer_email" sortColumn={sortColumn} sortDirection={sortDirection} onToggle={toggleSort} />
                                <SortHeader label="Status" column="status" sortColumn={sortColumn} sortDirection={sortDirection} onToggle={toggleSort} />
                                <SortHeader label="Created" column="created" sortColumn={sortColumn} sortDirection={sortDirection} onToggle={toggleSort} />
                                <SortHeader label="Expires" column="expires" sortColumn={sortColumn} sortDirection={sortDirection} onToggle={toggleSort} />
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={canDelete ? 7 : 6} className="text-center text-sm text-slate-500 py-10">
                                        Loading invites…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && invites.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canDelete ? 7 : 6} className="text-center text-sm text-slate-500 py-12" data-testid="invites-empty-state">
                                        <Mail className="mx-auto mb-3 text-slate-300" size={28} />
                                        {query || activeStatuses.size < ALL_STATUSES.length
                                            ? "No invites match the current filters."
                                            : "No invites yet — send your first one to get started."}
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading &&
                                invites.map((inv) => {
                                    const isDeletable = DELETABLE_STATUSES.has(inv.status);
                                    const isSelected = selectedIds.has(inv.id);
                                    return (
                                        <TableRow
                                            key={inv.id}
                                            data-testid={`invite-row-${inv.id}`}
                                            data-selected={isSelected ? "true" : undefined}
                                            className={isSelected ? "bg-blue-50/40" : undefined}
                                        >
                                            {canDelete && (
                                                <TableCell className="w-10">
                                                    <Checkbox
                                                        data-testid={`invite-select-${inv.id}`}
                                                        checked={isSelected}
                                                        onCheckedChange={() => toggleRow(inv.id)}
                                                        disabled={!isDeletable}
                                                        aria-label={
                                                            isDeletable
                                                                ? `Select invite for ${inv.customer_name}`
                                                                : `Cannot select — invite is ${inv.status}`
                                                        }
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell className="font-medium text-slate-900">{inv.customer_name}</TableCell>
                                            <TableCell className="text-slate-600">{inv.customer_email}</TableCell>
                                            <TableCell>
                                                <StatusBadge status={inv.status} />
                                            </TableCell>
                                            <TableCell className="text-slate-500 text-sm">{fmtDate(inv.created_at)}</TableCell>
                                            <TableCell className="text-slate-500 text-sm">{fmtDate(inv.expires_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    {inv.submission_id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            asChild
                                                            data-testid={`view-submission-from-invite-${inv.id}`}
                                                            className="gap-1.5"
                                                        >
                                                            <Link to={`/admin/submissions/${inv.submission_id}`}>
                                                                <FileCheck size={14} /> View
                                                            </Link>
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => copyLink(inv.invite_url)}
                                                        data-testid={`copy-invite-${inv.id}`}
                                                        className="gap-1.5"
                                                    >
                                                        <Copy size={14} /> Copy
                                                    </Button>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="px-2"
                                                                data-testid={`invite-more-${inv.id}`}
                                                            >
                                                                <MoreHorizontal size={16} />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-52">
                                                            <DropdownMenuItem
                                                                onSelect={() => resend(inv.id)}
                                                                disabled={busy || inv.status === "submitted" || inv.status === "deleted"}
                                                                data-testid={`invite-menu-resend-${inv.id}`}
                                                            >
                                                                <RefreshCcw size={14} className="mr-2" /> Resend invite
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onSelect={() => setRevokeTarget(inv)}
                                                                disabled={busy || inv.status === "submitted" || inv.status === "expired" || inv.status === "cancelled" || inv.status === "deleted"}
                                                                data-testid={`invite-menu-revoke-${inv.id}`}
                                                                className="text-amber-800"
                                                            >
                                                                <Trash2 size={14} className="mr-2" /> Revoke (make expired)
                                                            </DropdownMenuItem>
                                                            {canDelete && isDeletable && (
                                                                <>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        onSelect={() => setDeleteTarget(inv)}
                                                                        disabled={busy}
                                                                        data-testid={`invite-menu-delete-${inv.id}`}
                                                                        className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                                                    >
                                                                        <Trash2 size={14} className="mr-2" /> Delete permanently
                                                                    </DropdownMenuItem>
                                                                </>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Revoke (existing behavior — expire) */}
            <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
                <AlertDialogContent data-testid="revoke-invite-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke this invite?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {revokeTarget?.customer_name} ({revokeTarget?.customer_email}) will no longer be able to use this link. The invite will be marked as expired.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="revoke-invite-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="revoke-invite-confirm"
                            disabled={busy}
                            onClick={confirmRevoke}
                            className="bg-amber-600 hover:bg-amber-700"
                        >
                            {busy ? "Revoking…" : "Revoke invite"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete — single (spec 7A) */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-invite-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete this invite for {deleteTarget?.customer_name}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone from the UI. The invite row will be hidden from the list but retained in the database for audit purposes.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-invite-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="delete-invite-confirm"
                            disabled={busy}
                            onClick={confirmDeleteSingle}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {busy ? "Deleting…" : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete — bulk (spec 7B) */}
            <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
                <AlertDialogContent data-testid="bulk-delete-invites-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {selectedIds.size} invite{selectedIds.size === 1 ? "" : "s"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                <p className="mb-2">This cannot be undone from the UI.</p>
                                {selectedPreview.first5.length > 0 && (
                                    <ul className="text-xs list-disc pl-4 text-slate-600 space-y-0.5">
                                        {selectedPreview.first5.map((n) => (
                                            <li key={n} className="truncate">{n}</li>
                                        ))}
                                        {selectedPreview.extra > 0 && (
                                            <li className="italic text-slate-400">and {selectedPreview.extra} more…</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bulk-delete-invites-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="bulk-delete-invites-confirm"
                            disabled={busy}
                            onClick={confirmBulkDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {busy ? "Deleting…" : `Delete ${selectedIds.size} invite${selectedIds.size === 1 ? "" : "s"}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardShell>
    );
}
